import Dexie, { type Table } from 'dexie';
import type { Batch, Bill, BillLine, Customer, Movement, Offer, Product, Shift, StaffMember, Supplier } from '@sense/shared';

export interface StockRow { pid: number; qty: number }
export interface LocalBill extends Bill { synced: 0 | 1 }
export type CustomerDoc = Pick<Customer, 'id' | 'name' | 'phone' | 'email' | 'notes' | 'credit_limit'>;
/** Khata payment / adjustment made on a phone-only (Starter) store. */
export interface LedgerRow { id?: number; customer_id: string; ts: number; kind: 'payment' | 'adjust'; amount: number; method: string | null; note: string | null }
/**
 * Waiting to go to the server. bill / movement / customer / shift upload with every sync; product,
 * offer, store and ledger are only made on a phone-only (Starter) store and upload when it upgrades.
 */
export interface OutboxRow { seq?: number; kind: 'bill' | 'movement' | 'customer' | 'shift' | 'product' | 'offer' | 'store' | 'ledger'; doc: Bill | Movement | CustomerDoc | Shift | Product | Offer | LedgerRow | Record<string, unknown> }
export interface HeldBill { id?: number; ts: number; label: string; lines: CartLineSnapshot[]; customer: { id: string; name: string; phone: string } | string | null; billDiscount: number }
export interface CartLineSnapshot { pid: number; qty: number; price: number; disc: number; category?: string }
export interface MetaRow { key: string; value: unknown }

class SenseDB extends Dexie {
  products!: Table<Product, number>;
  stock!: Table<StockRow, number>;
  bills!: Table<LocalBill, string>;
  outbox!: Table<OutboxRow, number>;
  held!: Table<HeldBill, number>;
  meta!: Table<MetaRow, string>;
  offers!: Table<Offer, number>;
  staff!: Table<StaffMember & { active: boolean; canSignIn: boolean }, string>;
  customers!: Table<Customer, string>;
  shifts!: Table<Shift, string>;
  suppliers!: Table<Supplier, number>;
  batches!: Table<Batch, number>;
  ledger!: Table<LedgerRow, number>;

  constructor() {
    super('sense-daily');
    this.version(1).stores({
      products: '&id, sku, category, *barcodes, plu',
      stock: '&pid',
      bills: '&id, dev_no, ts, synced',
      outbox: '++seq, kind',
      held: '++id, ts',
      meta: '&key',
    });
    this.version(2).stores({ offers: '&id', staff: '&id' });
    this.version(3).stores({ customers: '&id, phone, name', shifts: '&id, opened_at, closed_at' });
    this.version(4).stores({ suppliers: '&id, name', batches: '&id, expiry' });
    this.version(5).stores({ ledger: '++id, customer_id, ts' });
  }
}

export const db = new SenseDB();

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return (row?.value as T) ?? fallback;
}

export function setMeta(key: string, value: unknown) {
  return db.meta.put({ key, value });
}

/**
 * Bill and movement counters belong to a terminal (device). A browser can hold one terminal per
 * store, so counters are keyed by device id and survive switching stores back and forth.
 */
export async function nextCounter(key: 'devNo' | 'movNo'): Promise<number> {
  const did = await getMeta<string>('deviceId', '');
  const k = `${key}:${did}`;
  const n = (await getMeta<number>(k, 0)) + 1;
  await setMeta(k, n);
  return n;
}

export async function peekCounter(key: 'devNo' | 'movNo'): Promise<number> {
  return getMeta<number>(`${key}:${await getMeta<string>('deviceId', '')}`, 0);
}

/** One-time move from the old single-terminal counters. */
export async function migrateCounters() {
  const did = await getMeta<string | null>('deviceId', null);
  for (const key of ['devNo', 'movNo'] as const) {
    const legacy = await db.meta.get(key);
    if (legacy && did) {
      if ((await getMeta<number | null>(`${key}:${did}`, null)) == null) await setMeta(`${key}:${did}`, legacy.value);
      await db.meta.delete(key);
    }
  }
  if (did) {
    const map = await getMeta<Record<string, string>>('devices', {});
    if (!Object.values(map).includes(did)) await setMeta('devices', { ...map, legacy: did });
  }
}

/** Apply stock deltas locally (sale = negative). */
export async function applyStock(lines: Array<[number, number]>) {
  for (const [pid, delta] of lines) {
    const cur = await db.stock.get(pid);
    await db.stock.put({ pid, qty: (cur?.qty ?? 0) + delta });
  }
}

export function billStockDeltas(items: BillLine[], kind: 0 | 1): Array<[number, number]> {
  return items.map(([pid, qty]) => [pid, kind === 1 ? qty : -qty]);
}

export async function resetLocalData() {
  await db.delete();
  localStorage.removeItem('sd.session');
  localStorage.removeItem('sd.cart');
}
